/**
 * COMPOSANT   : i18n setup (react-i18next)
 * OBJECTIF    : Fournir un système de traduction frontend qui charge le
 *               catalogue depuis /api/i18n/catalog?lang=X. Support RTL
 *               pour arabe. Persistance de la langue choisie dans
 *               localStorage.
 * ARCHITECTURE: Ressources chargées à la demande (lazy) depuis backend
 *               puis mises en cache local. Fallback FR.
 * API         :
 *   import i18n from '@/i18n'
 *   const { t } = useTranslation();
 *   t('section.executive_summary')
 *   i18n.changeLanguage('en')
 */
import i18n from "i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import { initReactI18next } from "react-i18next";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

const SUPPORTED = ["fr", "en", "pt", "ar"];
const RTL = new Set(["ar"]);

const loadedNamespaces = new Set();

async function loadLanguage(lng) {
  if (loadedNamespaces.has(lng)) return;
  try {
    const res = await axios.get(`${BACKEND_URL}/api/i18n/catalog?lang=${lng}`);
    const translations = res.data?.translations || {};
    // Aplatir "section.executive_summary" -> nested { section: { executive_summary: '...' } }
    const nested = {};
    Object.entries(translations).forEach(([k, v]) => {
      const parts = k.split(".");
      let cur = nested;
      for (let i = 0; i < parts.length - 1; i++) {
        if (!cur[parts[i]]) cur[parts[i]] = {};
        cur = cur[parts[i]];
      }
      cur[parts[parts.length - 1]] = v;
    });
    // Ajouter aussi les clés flat (avec _ à la place de .)
    const flatKeys = {};
    Object.entries(translations).forEach(([k, v]) => { flatKeys[k] = v; });
    i18n.addResourceBundle(lng, "translation", { ...nested, ...flatKeys }, true, true);
    loadedNamespaces.add(lng);
  } catch (e) {
    console.warn("i18n load failed for", lng, e);
  }
}

function updateHtmlDir(lng) {
  if (typeof document !== "undefined") {
    document.documentElement.dir = RTL.has(lng) ? "rtl" : "ltr";
    document.documentElement.lang = lng;
  }
}

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    fallbackLng: "fr",
    supportedLngs: SUPPORTED,
    interpolation: { escapeValue: false },
    detection: {
      order: ["localStorage", "navigator"],
      caches: ["localStorage"],
      lookupLocalStorage: "elia-lang",
    },
    resources: {
      fr: { translation: {} },
      en: { translation: {} },
      pt: { translation: {} },
      ar: { translation: {} },
    },
    react: { useSuspense: false },
  });

// Charger la langue initiale et écouter les changements
loadLanguage(i18n.language || "fr").then(() => updateHtmlDir(i18n.language || "fr"));
i18n.on("languageChanged", (lng) => {
  loadLanguage(lng).then(() => updateHtmlDir(lng));
});

export const SUPPORTED_LANGUAGES = SUPPORTED;
export const isRTL = (lng) => RTL.has(lng);

export default i18n;
